// 3rd party
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import useCart from 'selectors/cartSelectors';
import styled, { useTheme } from 'styled-components';
import { cloneDeep, set } from 'lodash';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

// Components
import { Button, Checkbox, TooltipWrapper, Typography } from 'components';
import CartListRowComponent from './CartListRow';

// Helpers
import { isCartVerificationExpired, isCartVerified } from 'common/helpers/v1';

// Types
import { Machine } from 'types';
import {
  CartState,
  CartListSelectionType,
  CartListViewType,
  CartPurchasable,
  CartListType
} from 'types/parts/cart';

// hooks
import useWindowDimensions from 'hooks/useWindowDimensions';

// Constants
import breakpoint from 'constants/breakpoints';

import { useGetProductsQuery, useGetMachinesQuery, useDeleteRemoteCartItemsMutation, useUpdateRemoteCartQuantityMutation } from 'api';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useDispatch } from 'react-redux';
import { cartActions } from 'actions';
import { JBTRoutes } from 'constants/routes';
import { UNIVERSAL_PRODUCTS_KEY } from 'constants/cart';
import Loader from 'components/Loader';
import { IcoShrink } from 'icons/indexed-icons/IcoShrink';
import { IcoExpand } from 'icons/indexed-icons/IcoExpands';
// import EmptyCartImg from 'icons/empty-cart.svg';
import theme from 'themes';
import { ToastMsg } from 'common/components/Toast';
import { EmptyCartImg } from 'icons/indexed-icons/EmptyCartImg';
import InformationCircle from 'icons/indexed-icons/InformationCircle';
import styledTheme from 'common/theme';
import { CustomText } from 'components/CartFulfillmentDetails';
import {
  CartPriceType,
  checkCartItemsPrice,
  getSelectedCartState
} from 'components/CartWizard/helper';
import { TentativeDate } from 'components/CartWizard/CartWizard.elements';
import ConfirmationModal from 'common/components/ConfirmationModal';
import TriangleWarning from 'icons/indexed-icons/TriangleWarning';
import { ENABLE_TWO_WAYS_SYNC } from 'components/CartWizard';
import CartPriceCalculation from './CartPriceCalculation';
import { OrderInputWithSFFlag, OrderInputWithPriceInfo, Product } from 'types/parts';

type CartListArguments = {
  handleClose?: () => void;
  viewType: CartListType;
  selectionType: CartListSelectionType;
  pmParts?: CartPurchasable[];
  selectAllControl?: JSX.Element;
  selectionHandler?: (item: CartPurchasable) => void;
  onCartViewType?: (
    cartViewType: CartListType.VIEW_TYPE_LESS | CartListType.VIEW_TYPE_MORE
  ) => void;
  onQtyChange?: (CartPurchasableList: CartPurchasable[]) => void;
  onSelectChange?: (CartPurchasableList: CartPurchasable[]) => void;
  isCartEmpty?: (status?: boolean) => void;
  isProcareEnable?: boolean;
  selectedPriceType?: CartPriceType;
  handleSelectAll?: (isAllSelected: boolean) => void;
  isFinalEstimationPage?: boolean;
  isAllSelected?: boolean;
  requestedDate?: string;
  enableProsealTwoWaySync?: boolean;
  orderInputData?: OrderInputWithSFFlag;
  setOrderInputData?: (data: OrderInputWithSFFlag | undefined) => void;
  setOrderPriceInfo?: ({
    pricedInfo,
    nonePricedInfo
  }: {
    pricedInfo?: OrderInputWithPriceInfo;
    nonePricedInfo?: OrderInputWithPriceInfo;
  }) => void;
  setPriceCalculationStatus?: (loading: boolean) => void;
  setSelectedCartPriceType?: (priceType: CartPriceType) => void;
};

const QUANTITY_UPDATE_THROTTLE_MS = 250;

const FullListContainer = styled.div`
  table:first-of-type {
    margin-top: 2.375rem;
  }
  table > h2 {
    margin-bottom: 0.5rem;
  }

  position: relative;
  overflow: auto;
  height: 100%;
  @media (max-width: ${breakpoint.size.md}px) {
    table:first-of-type {
      margin-top: 1rem;
    }
  }
  .cart-note {
    position: absolute;
    padding: 4rem 0;
    width: 100%;
    text-align: center;
  }
`;

const CartListContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0rem 2.375rem 0rem 2.375rem;
  overflow: auto;
  ${({ defaultValue }) =>
    defaultValue !== CartListType.VIEW_TYPE_DEFAULT &&
    `
    margin: 0;
  `}
`;

const CartListRowsContainer = styled.div``;

export const RemoveFromCartIcon = styled.div`
  display: block;
  text-align: center;
  align-items: center;
  // min-width: 10%;
  max-width: 10rem;
  cursor: pointer;
  margin: 0.5rem 0 0 1rem;
`;

export const CartItemRowHeader = styled.div<{ viewType?: CartListType }>`
  display: flex;
  align-items: center;
  height: 3.25rem;
  font-weight: 600;
  p {
    margin-bottom: 0;
  }
  border-collapse: separate;
  border-spacing: 0;
  border-bottom: 0.0625rem solid ${theme.colors.lightGrey6};
  border-top: 0.0625rem solid ${theme.colors.lightGrey6};
  ${({ viewType }) =>
    viewType === CartListType.VIEW_TYPE_MORE &&
    `
    > div {
      width: 20% !important;
    }
  `}
  > div {
    flex-shrink: 0;
    flex-grow: 0;
  }
`;

export const CartItemRow = styled.div<{ viewType?: CartListType }>`
  display: flex;
  position: relative; // pop ups will be positioned relative to the row they're a child of
  align-items: center;
  justify-content: start;
  border-bottom: 0.0625rem solid ${({ theme }) => theme.colors.lightGrey6};
  position: relative;
  > div {
    flex-grow: 0;
    flex-shrink: 0;
  }
  ${({ viewType }) =>
    viewType === CartListType.VIEW_TYPE_MORE &&
    `
    > div {
      width: 20% !important;
    }
  `}
`;

export const CartUrgentText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.2rem;
  min-width: 6%;
  max-width: 6%;
  margin: 0;
  text-align: left;
  justify-content: start;
  svg {
    vertical-align: middle;
  }
  .urgent-info {
    text-align: center;
    padding: 0 1rem;
  }
`;

export const UrgentText = styled.div`
  display: flex;
  .urgent {
    display: flex;
    align-items: center;
    margin: 0;
    gap: 0.5rem;
  }
`;
export const CartItemText = styled.div<{ defaultValue?: string }>`
  display: flex;
  flex-direction: column;
  word-break: break-all;
  flex-grow: 0;
  flex-shrink: 0;
  width: ${({ defaultValue }) => (defaultValue === CartListType.VIEW_TYPE_MORE ? '14%' : '18%')};
  p {
    margin-bottom: 0;
  }
  ${({ defaultValue }) =>
    defaultValue === CartListType.VIEW_TYPE_LESS &&
    `
    width: 40%;
    min-width: 15%;
  `}

  ${({ defaultValue }) =>
    defaultValue === CartListType.VIEW_TYPE_DEFAULT &&
    `
    @media (max-width: ${breakpoint.size.md}px) {
    width: 100%;
  }
  `}
`;

export const PriceText = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1 !important;
  p {
    margin-bottom: 0;
  }
  .total-price {
    word-wrap: break-word;
  }
  ${({ defaultValue }) =>
    defaultValue === CartListType.VIEW_TYPE_LESS
      ? `
    min-width: 25%;
    max-width: 30%;
  `
      : `min-width: 15%;
  max-width: 15%;`}

  ${({ defaultValue }) =>
    defaultValue === CartListType.VIEW_TYPE_DEFAULT &&
    `
    @media (max-width: ${breakpoint.size.md}px) {
    margin: 0;
    min-width: 100%;
    max-width: 100%;
  }
  `}
  .part-price {
    flex-direction: row;
    align-items: center;
  }
`;
export const PriceItem = styled.div`
  display: flex;
  align-items: center;
  .part-price {
    flex-direction: row;
    align-items: center;
  }
  .price {
    width: 50%;
  }
`;
export const CartItemSku = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 0;
  flex-shrink: 0;
  width: 10%;
  word-wrap: break-word;
  padding: 0;

  @media (max-width: ${breakpoint.size.md}px) {
    width: 50%;
  }
`;

export const CartStock = styled.div`
  min-width: 7rem;
  width: 10%;
`;
export const AvgLeadTime = styled.div`
  width: 15%;
`;
export const CartItemStock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 15%;
  max-width: 15%;
  p {
    margin-bottom: 0;
    padding: 0;
  }
`;

export const QuantityContainer = styled.div`
  width: 8%;
  flex-grow: 0;
  flex-shrink: 0;
  ${({ defaultValue }) =>
    defaultValue === CartListType.VIEW_TYPE_LESS &&
    `
    min-width: 30%;
    max-width: 25%;
  `};
  ${({ defaultValue }) =>
    defaultValue === CartListType.VIEW_TYPE_DEFAULT &&
    `
   @media (max-width: ${breakpoint.size.md}px) {
    width: 50%;
    padding: 0rem 0;
  }
  `};
  input {
    padding-left: 0.5rem;
    padding-right: 0rem;
    width: 3.5rem;
    margin-left: 10px;
    height: 2.5rem;
  }
`;

export const CommentsText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 20%;
  max-width: 20%;
  p {
    margin-bottom: 0;
  }
  ${({ defaultValue }) =>
    defaultValue === CartListType.VIEW_TYPE_LESS &&
    `
    width: 0%;
    display: none;
  `};
`;

export const SelectControl = styled.div<{ width?: string }>`
  display: flex;
  flex-direction: column;
  width: ${({ width }) => width || '10%'};
  text-align: left;
  align-self: center;
  justify-content: flex-start;
  div {
    align-self: flex-start;
  }
  p {
    margin: 0;
  }
`;

export const CheckboxContainer = styled.div`
  margin-left: 1rem;
  @media (max-width: ${breakpoint.size.md}px) {
    margin-left: 0;
  }
`;
const SelectAllControl = styled.div`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.buttons.primary.fill};
  font-size: ${({ theme }) => theme.typography.text.bodyMediumBold.size};
  font-weight: ${({ theme }) => theme.typography.components.tableHeader.weight};
  line-height: ${({ theme }) => theme.typography.components.tableHeader.lineHeight};
  font-family: ${({ theme }) => theme.typography.family};
  text-align: left;
  align-self: flex-start;
`;

const TotalRow = styled.div`
  @media (max-width: ${breakpoint.size.md}px) {
    display: flex;
    justify-content: right;
    margin: 0.5rem 0rem;
  }
`;

export const TotalText = styled.div<{ marginTop?: string; justifyContent?: string }>`
  display: flex;
  flex-shrink: 1;
  align-items: center;
  gap: 0.5rem;
  justify-content: ${({ justifyContent }) => justifyContent || 'end'};
  margin-top: ${({ marginTop }) => marginTop || '2rem'};

  @media (max-width: ${breakpoint.size.md}px) {
    justify-content: center;
    margin-top: 1rem;
  }
`;

const ViewTypeText = styled.div`
  display: flex;
  align-self: end;
  cursor: pointer;
  h6 {
    margin-top: 2rem;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.colors.mediumBlue};
  }
  @media (max-width: ${breakpoint.size.md}px) {
    align-self: center;
  }
`;

const Box = styled.div`
  align-items: center;
  height: 100%;
  justify-content: center;
  display: flex;
`;

const EmptyCartBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
`;

const EmptyCartIcon = styled.div`
  display: flex;
`;

const EmptyCartText = styled.div`
  margin: 2rem 0;
`;

const EmptyCartButton = styled.div`
  display: flex;
  padding-bottom: 1.25rem;
`;
const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.2rem;
`;

export const ProCareAllowance = styled.div<{ disabled?: boolean }>`
  width: 9%;
  display: flex;
  align-items: center;
  opacity: ${({ disabled }) => (disabled ? '.5' : '1')};
`;
const CartListHeader = styled.div`
  display: flex;
  align-items: center;
`;
const CartOrderNumber = styled.div`
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  color: ${({ theme }) => theme.colors.primaryBlue5};
  margin-left: auto;
`;
const HeaderRowComponent = ({
  viewType,
  selectionType,
  selectAllControl,
  handleSelectAll,
  isAllSelected,
  isProcareEnable,
  isFinalEstimationPage
}: CartListArguments): JSX.Element => {
  const { t } = useTranslation(['fpns']);
  return (
    <CartItemRowHeader viewType={viewType} style={{ marginBottom: 0 }}>
      {/* {viewType === CartListType.VIEW_TYPE_DEFAULT && <RemoveFromCartIcon></RemoveFromCartIcon>} */}
      {viewType === CartListType.VIEW_TYPE_DEFAULT &&
        selectionType === CartListType.SELECTION_MULTI_SELECT &&
        !isFinalEstimationPage && (
          <SelectControl width="3rem">
            <Checkbox
              key={`checkbox-all`}
              checked={isAllSelected}
              onChange={(e) => {
                handleSelectAll?.(e.target.checked);
                // selectionHandler && selectionHandler(cartItem.item.id);
              }}
              width={18}
              height={18}
            />
          </SelectControl>
        )}
      {viewType === CartListType.VIEW_TYPE_MORE &&
        selectionType === CartListType.SELECTION_MULTI_SELECT && (
          <SelectControl>{selectAllControl}</SelectControl>
        )}

      <CartItemText defaultValue={viewType}>
        <Typography weight="semi-bold">{t('parts_table_product')}</Typography>
      </CartItemText>
      {(viewType === CartListType.VIEW_TYPE_DEFAULT ||
        viewType === CartListType.VIEW_TYPE_MORE) && (
        <CartItemSku>
          <Typography weight="semi-bold">{t('parts_table_sku')}</Typography>
        </CartItemSku>
      )}
      {(viewType === CartListType.VIEW_TYPE_DEFAULT ||
        viewType === CartListType.VIEW_TYPE_MORE) && (
        <CartStock>
          <Typography weight="semi-bold">{'Stock'}</Typography>
        </CartStock>
      )}
      {viewType === CartListType.VIEW_TYPE_DEFAULT && (
        <AvgLeadTime>
          <Typography weight="semi-bold">{'Avg. Lead Time'}</Typography>
        </AvgLeadTime>
      )}
      <QuantityContainer defaultValue={viewType}>
        <Typography weight="semi-bold">{t('parts_table_quantity')}</Typography>
      </QuantityContainer>
      <PriceText defaultValue={viewType} style={{ padding: 0 }}>
        <Typography weight="semi-bold">{t('parts_table_price')}</Typography>
      </PriceText>
      {viewType === CartListType.VIEW_TYPE_DEFAULT && isProcareEnable && (
        <ProCareAllowance>
          <Typography className="p-0" weight="semi-bold">
            {'ProCare Allowance'}
          </Typography>
        </ProCareAllowance>
      )}
      {viewType === CartListType.VIEW_TYPE_DEFAULT && (
        <CartUrgentText defaultValue={viewType}>
          <Typography className="urgent-header" weight="semi-bold">
            {t('parts_table_urgent')}
          </Typography>
          <TooltipWrapper
            top
            Tooltip={<CustomText>{t('parts_urgent_message')}</CustomText>}
            textColor={theme.colors.textLightBlack}
            backGroundColor={theme.colors.white}
          >
            <InformationCircle />
          </TooltipWrapper>
          
        </CartUrgentText>
      )}
      {isFinalEstimationPage && viewType === CartListType.VIEW_TYPE_DEFAULT && (
        <TentativeDate defaultValue={viewType}>
          <Typography className="urgent-header" weight="semi-bold">
            {t('Tentative Delivery Date')}
          </Typography>
        </TentativeDate>
      )}
      {/* {viewType === CartListType.VIEW_TYPE_DEFAULT &&
        enableProsealTwoWaySync &&
        isFinalEstimationPage && (
          <TentativeDate defaultValue={viewType}>
            <Typography className="urgent-header" weight="semi-bold">
              {t('Tentative Delivery Date')}
            </Typography>
          </TentativeDate>
        )} */}
      {/* {viewType === CartListType.VIEW_TYPE_MORE && (
        <CommentsText defaultValue={viewType}>
          <Typography variant="body1">{t('parts_table_comments')}</Typography>
        </CommentsText>
      )} */}
    </CartItemRowHeader>
  );
};

function organizeByGroup(cartItems: CartPurchasable[]) {
  const retMap = new Map<string, CartPurchasable[]>();
  retMap.set(UNIVERSAL_PRODUCTS_KEY, []);

  cartItems.forEach((cartItem) => {
    if (!cartItem.groupId) retMap.get(UNIVERSAL_PRODUCTS_KEY)?.push(cartItem);
    else {
      if (retMap.has(cartItem.groupId)) {
        retMap.get(cartItem.groupId)?.push(cartItem);
      } else {
        retMap.set(cartItem.groupId, [cartItem]);
      }
    }
  });

  return retMap;
}

function organizeCartByGroup(cartState: CartState | null): Map<string, CartPurchasable[]> {
  if (!cartState) {
    return new Map();
  }
  return organizeByGroup(cartState.items);
}

function organizePMPartsByMachine(pmParts: CartPurchasable[]): Map<string, CartPurchasable[]> {
  const pmMap = organizeByGroup(pmParts);
  pmMap.delete(UNIVERSAL_PRODUCTS_KEY);
  return pmMap;
}

function getMachineDisplayName(
  machineKey: string,
  machineInfo: Machine[] | undefined,
  machineInfoFetching: boolean,
  t: TFunction<'fpns'[], undefined>
) {
  if (machineKey === UNIVERSAL_PRODUCTS_KEY) return t('universal_products');
  else if (machineInfoFetching) return <i>{t('machine_name_loading')}</i>;
  return machineInfo !== undefined
    ? machineInfo?.filter((m) => m.id === machineKey)[0].description
    : t('machine_name_not_available');
}

const CartListComponent = ({
  viewType,
  handleClose,
  selectionType,
  pmParts,
  onCartViewType,
  onQtyChange,
  onSelectChange,
  isCartEmpty,
  selectedPriceType,
  requestedDate,
  isFinalEstimationPage,
  enableProsealTwoWaySync,
  orderInputData,
  setPriceCalculationStatus,
  setOrderInputData,
  setOrderPriceInfo
}: CartListArguments): ReactElement => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation(['fpns']);
  const cartState = useCart() as CartState;
  const [viewTypeState, setViewTypeState] = useState(viewType);
  const [removeCartItem, setRemoveCartItem] = useState<CartPurchasable>();
  const [orderNumber, setOrderNumber] = useState('');
  const [priceCalculationLoading, setPriceCalculationLoading] = useState(true);
  const [pronetData, setPronetData] = useState<{
    pricedInfo?: OrderInputWithPriceInfo;
    nonePricedInfo?: OrderInputWithPriceInfo;
  }>();
  // start here
  // update setOrderPriceInfo def in catstepone and cartwizard
  // make necessary modification in cartwizard to show green banner
  // if test is successful, make prince and none price order calls
  // follow upwith order confirmation calls
  // who separate order numbers form confirmation calls
  const [localCartItems, updateLocalCartItems] = useState<CartPurchasable[]>([]);
  const [cartUpdateLoading, setCartUpdateLoading] = useState(false);
  const quantitySyncRef = useRef<
    Record<
      string,
      {
        inFlight: boolean;
        pendingQuantity?: number;
        timer?: ReturnType<typeof setTimeout>;
      }
    >
  >({});
  const { width: screenWidth } = useWindowDimensions();
  const [isAllSelected, setIsAllSelected] = useState(
    cartState.items.every((product) => product.selected)
  );

  useEffect(() => {
    setIsAllSelected(cartState.items.every((product) => product.selected));
  }, [cartState.items]);

  const cart = getSelectedCartState(cartState, selectedPriceType);
  const cartPriceType = useMemo(() => checkCartItemsPrice(cartState), [cartState]);
  const productUUIDs = cart?.items.map((item) => item.item.id);
  // Moving the hook call outside of the condition, as hooks should not be conditionally called
  // (surprised we did not get a "too few hooks" react error).
  // Will use skipToken to handle cases where we do not want this data.

  const { data: verificationProductsResults, error: productError } = useGetProductsQuery(
    viewType === CartListType.VIEW_TYPE_DEFAULT && productUUIDs && productUUIDs.length > 0
      ? { productIds: productUUIDs }
      : skipToken
  );
 const [deleteRemoteCartItems] = useDeleteRemoteCartItemsMutation();
 const [updateRemoteCartQuantity] = useUpdateRemoteCartQuantityMutation();

  const getQuantitySyncKey = (item: CartPurchasable): string => {
    return `${item.item.id}::${item.groupId || UNIVERSAL_PRODUCTS_KEY}`;
  };

  const processQuantityUpdate = (key: string, id: string) => {
    const entry = quantitySyncRef.current[key];
    if (!entry || entry.inFlight || typeof entry.pendingQuantity === 'undefined') return;

    const nextQuantity = entry.pendingQuantity;
    entry.pendingQuantity = undefined;
    entry.inFlight = true;

    updateRemoteCartQuantity({ id, quantity: nextQuantity })
      .unwrap()
      .catch((error) => {
        console.error('Error updating quantity:', error);
      })
      .finally(() => {
        const latestEntry = quantitySyncRef.current[key];
        if (!latestEntry) return;

        latestEntry.inFlight = false;

        // Immediately flush the latest queued value after an in-flight update completes.
        if (typeof latestEntry.pendingQuantity !== 'undefined') {
          if (latestEntry.timer) {
            clearTimeout(latestEntry.timer);
            latestEntry.timer = undefined;
          }
          processQuantityUpdate(key, id);
        }
      });
  };

  const queueQuantityUpdate = (item: CartPurchasable) => {
    const key = getQuantitySyncKey(item);
    const existing = quantitySyncRef.current[key] || { inFlight: false };
    existing.pendingQuantity = item.quantity;

    if (existing.timer) {
      clearTimeout(existing.timer);
    }

    existing.timer = setTimeout(() => {
      const latestEntry = quantitySyncRef.current[key];
      if (latestEntry) latestEntry.timer = undefined;
      processQuantityUpdate(key, item.item.id);
    }, QUANTITY_UPDATE_THROTTLE_MS);

    quantitySyncRef.current[key] = existing;
  };

  useEffect(() => {
    return () => {
      Object.values(quantitySyncRef.current).forEach((entry) => {
        if (entry?.timer) clearTimeout(entry.timer);
      });
      quantitySyncRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (viewType === CartListType.VIEW_TYPE_DEFAULT && verificationProductsResults) {
      const verificationProducts = verificationProductsResults.items;
      if (productError) {
        ToastMsg({
          heading: 'Warning',
          message: 'Cart cannot be verified.',
          type: 'warning',
          toastId: 'cant-be-verified'
        });
      } else if (
        verificationProducts.length > 0 &&
        (isCartVerificationExpired(cart) || !isCartVerified(cart))
      ) {
        dispatch({ type: cartActions.VERIFY_CART, verificationItems: verificationProducts });
      }
    }
  }, [verificationProductsResults]);

  useEffect(() => {
    if (pronetData) {
      const { pricedInfo, nonePricedInfo } = pronetData;
      // deep copy cart items to avoid directly mutating the cart state which can cause issues with react state management
      const parts = [...(pricedInfo?.parts || []), ...(nonePricedInfo?.parts || [])];
      const updatedCartItems = cart.items.map((cartItem) => {
        const matchedPart = parts.find((part) => part.prosealPartNumber === cartItem.item.sku);
        if (matchedPart) {
          return {
            ...cartItem,
            item: {
              ...cartItem.item,
              price: matchedPart.unitValueExTax
            }
          };
        }
      });

      // dispatch updated items to the cart state
      if (updatedCartItems && updatedCartItems.length > 0) {
        dispatch({
          type: cartActions.UPDATE_CART_ITEMS,
          updatedItems: updatedCartItems
        });
      }
    }
  }, [pronetData]);
  const cartDataByGroup = pmParts ? organizePMPartsByMachine(pmParts) : organizeCartByGroup(cart);
  let groupIds: string[] = cart?.items
    ? cart.items.map((item) => item.groupId || UNIVERSAL_PRODUCTS_KEY)
    : [];
  // Remove the universal group id, if present
  groupIds = groupIds.filter((id) => id !== UNIVERSAL_PRODUCTS_KEY);
  // At the moment, only grouping by machine IDs, so use the machines by IDs query
  const { data: machineInfo, isFetching: machineInfoFetching } = useGetMachinesQuery(
    groupIds.length === 0
      ? skipToken
      : {
          uuids: groupIds,
          includeResources: false
        }
  );
  if (
    cartDataByGroup.size === 1 &&
    cartDataByGroup.get(UNIVERSAL_PRODUCTS_KEY)?.length === 0 &&
    cartState.items.length
  ) {
    isCartEmpty?.(true);
  }

  const isProcareEnable = useMemo(() => {
    const findProcareItem = cart.items.find((item) => item.item.procareAllowance);
    return !!findProcareItem;
  }, [cart]);

  const handleSelectAll = (isAllSelected: boolean) => {
    setIsAllSelected(isAllSelected);
    dispatch({
      type: cartActions.UPDATE_CART,
      selected: isAllSelected
    });
  };
  const setPriceInfo = (data: {
    pricedInfo?: OrderInputWithPriceInfo;
    nonePricedInfo?: OrderInputWithPriceInfo;
  }) => {
    if (setOrderPriceInfo) {
      setOrderPriceInfo(data);
    }
    setOrderNumber(data.pricedInfo?.erpOrderNumber || data.nonePricedInfo?.erpOrderNumber || '');
  };
  const updatePriceCalculationLoading = (loading: boolean) => {
    setPriceCalculationLoading(loading);
    setPriceCalculationStatus?.(loading);
  };
  const updateCartPriceWithPronetData = (pronetData: {
    pricedInfo?: OrderInputWithPriceInfo;
    nonePricedInfo?: OrderInputWithPriceInfo;
  }) => {
    setPronetData(pronetData);
    setPriceInfo(pronetData);
  };
  return (
    <FullListContainer>
      {Array.from(cartDataByGroup.entries()).map((entry) => {
        const [group, items] = entry;

        const currencyGroups: Record<string, number> = {};
        items.forEach((itemGroup) => {
          const priceUnit = itemGroup.item.priceUnit || 'USD';
          const price = !itemGroup.item?.isPurchasable
            ? 0
            : (itemGroup.item?.custPrice
                ? parseFloat(itemGroup.item.custPrice.toFixed(2))
                : parseFloat(itemGroup.item?.price?.toFixed(2) || '0') || 0) * itemGroup.quantity;
          if (price) {
            if (Object.prototype.hasOwnProperty.call(currencyGroups, priceUnit))
              currencyGroups[priceUnit] += price;
            else currencyGroups[priceUnit] = price;
          }
        });

        // const subTotalCost = Object.keys(currencyGroups)
        //   .map((priceUnit) => {
        //     return currency(currencyGroups[priceUnit], priceUnit);
        //   })
        //   .join(', ');
        if (pmParts && localCartItems.length === 0) {
          updateLocalCartItems(
            pmParts.map((cartItem) => {
              return {
                ...cartItem,
                selected: true
              };
            })
          );
        }
        const viewTypeClickHandler = () => {
          const vtState =
            viewTypeState === CartListType.VIEW_TYPE_LESS
              ? CartListType.VIEW_TYPE_MORE
              : CartListType.VIEW_TYPE_LESS;
          setViewTypeState(vtState);
          onCartViewType && onCartViewType(vtState);
          onSelectChange && onSelectChange(localCartItems);
        };
        // Create the control component for (de)selecting all
        const selectAllControl = (
          <SelectAllControl
            role="button"
            onClick={() => {
              const updatedCartItems = localCartItems.some((cartItem) => !!cartItem.selected)
                ? localCartItems.map((cartItem) => {
                    return { ...cartItem, selected: false };
                  })
                : localCartItems.map((cartItem) => {
                    return { ...cartItem, selected: true };
                  });
              updateLocalCartItems(updatedCartItems);
              onSelectChange && onSelectChange(updatedCartItems);
            }}
          >
            {localCartItems.some((cartItem) => cartItem.selected) ? 'Deselect All' : 'Select All'}
          </SelectAllControl>
        );
        const checkHandler = (cartItem: CartPurchasable) => {
          const updatedItem = cart.items.find(
            (item) =>
              item.item.id === cartItem.item.id &&
              item.item.priceUnit === cartItem.item.priceUnit &&
              item.item.custPrice === cartItem.item.custPrice &&
              item.item.price === cartItem.item.price &&
              item.item.uniqueId === cartItem.item.uniqueId
          );
          dispatch({
            type: cartActions.UPDATE_CART_ITEM,
            item: updatedItem?.item,
            selected: !updatedItem?.selected,
            urgent: updatedItem?.urgent,
            procareAllowance: updatedItem?.procareAllowance,
            groupId: updatedItem?.groupId
          });
          setIsAllSelected(false);
        };
        const urgentChangeHandler = (item: CartPurchasable) => {
          const updatedItem = { ...item };
          typeof updatedItem.urgent === 'undefined'
            ? (updatedItem.urgent = true)
            : (updatedItem.urgent = !updatedItem.urgent);
          dispatch({
            type: cartActions.UPDATE_CART_ITEM,
            item: updatedItem.item,
            urgent: updatedItem.urgent,
            procareAllowance: updatedItem.procareAllowance,
            selected: updatedItem.selected,
            groupId: updatedItem.groupId
          });
        };
        const handleProCareAllowance = (item: CartPurchasable) => {
          const updatedItem = { ...item };
          typeof updatedItem.procareAllowance === 'undefined'
            ? (updatedItem.procareAllowance = true)
            : (updatedItem.procareAllowance = !updatedItem.procareAllowance);
          dispatch({
            type: cartActions.UPDATE_CART_ITEM,
            item: updatedItem.item,
            urgent: updatedItem.urgent,
            procareAllowance: updatedItem.procareAllowance,
            groupId: updatedItem.groupId
          });
        };
        const removeHandler = (item: CartPurchasable) => {
          setRemoveCartItem(undefined);
          const key = getQuantitySyncKey(item);
          const queueEntry = quantitySyncRef.current[key];
          if (queueEntry?.timer) clearTimeout(queueEntry.timer);
          delete quantitySyncRef.current[key];

          deleteRemoteCartItems(item.item.id).unwrap().then(() => {
              dispatch({
              type: cartActions.REMOVE_FROM_CART,
              item: item.item,
              quantity: item.quantity,
              groupId: item.groupId
            });
          });
          
        };
        const qtyChangeHandler = (item: CartPurchasable, isCartUpdateLoading: boolean) => {
          setCartUpdateLoading(isCartUpdateLoading);
          const updatedCartItems = cloneDeep(localCartItems);
          const itemIndex = localCartItems.findIndex(
            (cartItem) => cartItem.item.id === item.item.id && cartItem.groupId === item.groupId
          );
          updatedCartItems[itemIndex] = { ...item };
          updateLocalCartItems(updatedCartItems);

          // Queue backend quantity updates to avoid out-of-order requests during rapid input changes.
          queueQuantityUpdate(item);

          onQtyChange && onQtyChange(updatedCartItems);
        };

        const rowItems = viewType === CartListType.VIEW_TYPE_DEFAULT ? items : localCartItems;

        if (rowItems.length !== 0)
          return (
            <div style={{ position: 'relative' }} key={group}>
              <CartListContainer defaultValue={viewType}>
                {viewType === CartListType.VIEW_TYPE_DEFAULT && (
                  <CartListHeader>
                    <Typography variant="h2">
                      {getMachineDisplayName(group, machineInfo, machineInfoFetching, t)}
                    </Typography>
                    {isFinalEstimationPage && (
                      <CartOrderNumber>
                        {priceCalculationLoading ? (
                          <Loader size={40} />
                        ) : (
                          <>{orderNumber ? t('cart_order_number') + orderNumber : ''}</>
                        )}
                      </CartOrderNumber>
                    )}
                  </CartListHeader>
                )}
                {viewType !== CartListType.VIEW_TYPE_DEFAULT && (
                  <ViewTypeText onClick={viewTypeClickHandler}>
                    <Typography variant="h6">
                      {viewTypeState === CartListType.VIEW_TYPE_LESS ? (
                        <IconContainer>
                          {' '}
                          <IcoExpand width="18" />
                          {t('expand')}
                        </IconContainer>
                      ) : (
                        <IconContainer>
                          <IcoShrink width="18" /> {t('shrink')}
                        </IconContainer>
                      )}
                    </Typography>
                  </ViewTypeText>
                )}
                {viewType === CartListType.VIEW_TYPE_DEFAULT &&
                screenWidth &&
                screenWidth <= 768 ? null : (
                  <HeaderRowComponent
                    viewType={viewTypeState}
                    selectionType={selectionType}
                    selectAllControl={selectAllControl}
                    isProcareEnable={isProcareEnable}
                    handleSelectAll={handleSelectAll}
                    isAllSelected={isAllSelected}
                    requestedDate={requestedDate}
                    isFinalEstimationPage={isFinalEstimationPage}
                    enableProsealTwoWaySync={enableProsealTwoWaySync}
                  />
                )}
                <CartListRowsContainer>
                  {cartUpdateLoading && <Loader />}
                  {!cartUpdateLoading &&
                    rowItems.map((purchasable, i) => {
                      return (
                        purchasable.item && (
                          <CartListRowComponent
                            viewType={viewTypeState}
                            key={`${purchasable.item.sku}-${i}`}
                            purchasable={purchasable}
                            selectionType={selectionType}
                            selectionHandler={checkHandler}
                            removeHandler={setRemoveCartItem}
                            qtyChangeHandler={qtyChangeHandler}
                            urgentChangeHandler={urgentChangeHandler}
                            handleProCareAllowance={
                              isProcareEnable ? handleProCareAllowance : undefined
                            }
                            handleClose={handleClose}
                            selectedPriceType={selectedPriceType}
                            requestedDate={requestedDate}
                            isFinalEstimationPage={isFinalEstimationPage}
                            enableProsealTwoWaySync={enableProsealTwoWaySync}
                          />
                        )
                      );
                    })}
                </CartListRowsContainer>
                {rowItems.length != 0 &&
                  viewType === CartListType.VIEW_TYPE_DEFAULT &&
                  (isFinalEstimationPage && ENABLE_TWO_WAYS_SYNC ? (
                    <CartPriceCalculation
                      orderInputData={orderInputData}
                      setPriceCalculationLoading={updatePriceCalculationLoading}
                      setOrderInputData={setOrderInputData}
                      setOrderPriceInfo={updateCartPriceWithPronetData}
                      selectedPriceType={selectedPriceType}
                      cartPriceType={cartPriceType}
                    />
                  ) : (
                    <></>
                    // <TotalRow>
                    //   <TotalText>
                    //     <Typography
                    //       weight="medium"
                    //       mb={0}
                    //       size={'1.2rem'}
                    //       color={lightTheme.gray700}
                    //     >
                    //       {t('machine_subtotal')}:{' '}
                    //     </Typography>
                    //     <Typography weight="medium" mb={0} size={'1.2rem'}>
                    //       {subTotalCost !== '' ? String(subTotalCost) : 'N/A'}
                    //     </Typography>
                    //   </TotalText>
                    // </TotalRow>
                  ))}
              </CartListContainer>
              {!!removeCartItem && (
                <ConfirmationModal
                  isConfirmPrompt
                  isVisible={!!removeCartItem}
                  title={'Delete Product?'}
                  headerIcon={<TriangleWarning />}
                  zIndex={99999}
                  onCancelCallback={() => setRemoveCartItem(undefined)}
                  onConfirmCallback={() => removeHandler(removeCartItem)}
                >
                  <Typography mb={0}>Are you sure you wish to delete the product?</Typography>
                </ConfirmationModal>
              )}
            </div>
          );
      })}
      {viewType === CartListType.VIEW_TYPE_DEFAULT &&
      !(cartDataByGroup.size === 1 && cartDataByGroup.get(UNIVERSAL_PRODUCTS_KEY)?.length === 0) &&
      !isFinalEstimationPage ? (
        enableProsealTwoWaySync ? (
          <Typography className="cart-note" color={styledTheme.colors.gray800} weight="semi-bold">
            Product price may vary on Final Estimate*
          </Typography>
        ) : (
          <Typography className="cart-note" color={styledTheme.colors.gray800} weight="semi-bold">
            Shipping and tax to be calculated separately*
          </Typography>
        )
      ) : null}
      {cartDataByGroup.size === 1 && cartDataByGroup.get(UNIVERSAL_PRODUCTS_KEY)?.length === 0 && (
        <Box>
          <EmptyCartBox>
            <EmptyCartIcon>
              <EmptyCartImg />
            </EmptyCartIcon>
            <EmptyCartText>
              <Typography size={'1rem'} weight="semi-bold" color={theme.colors.primaryBlue5}>
                {t('cart_empty')}
              </Typography>
            </EmptyCartText>
            <EmptyCartButton>
              <Button
                variant="hover-blue"
                borderRadius="3rem"
                bgColor={theme.colors.mediumBlue}
                onClick={() => window.location.assign(JBTRoutes.partsCatalog)}
              >
                {t('browse_parts_store')}
              </Button>
            </EmptyCartButton>
          </EmptyCartBox>
        </Box>
      )}
    </FullListContainer>
  );
};

export default CartListComponent;
